import {
  Backdrop,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import MDBox from "components/MDBox";
import DataTable from "comps/Tables/DataTable";
import GetLanguageList from "hooks/LanguageList/GetLanguageList";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import TLinkApi from "services/tlink.api";

const SupplyApiSurvey = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filterData, setFilterData] = useState(data);
  const [totalLength, setTotalLength] = useState(0);
  const [entriesPerPage, setEntriesPerPage] = useState(200);
  const [page, setPage] = useState(1);
  const [langaugeId, setLangaugeId] = useState(-1);
  const { id } = useParams();
  const { language } = GetLanguageList();

  useEffect(() => {
    const d = data.slice(
      Math.max(0, (page - 1) * Math.max(1, entriesPerPage)), // start index
      Math.min(
        data.length,
        Math.max(0, (page - 1) * Math.max(1, entriesPerPage)) +
          Math.max(1, entriesPerPage) // end index
      )
    );
    setFilterData(d);
  }, [page, entriesPerPage, data]);

  useEffect(() => {
    setPage(1);
  }, [entriesPerPage]);
  const getData = async () => {
    try {
      setIsLoading(true);
      const response = await TLinkApi.get(`/vendors/${id}/supply-api/surveys`);
      setData(response?.data);
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (langaugeId > 0) {
      const filtered = data.filter(
        (item) => item.language_id === Number(langaugeId)
      );
      setTotalLength(filtered?.length);
      setFilterData(filtered);
    } else {
      setTotalLength(data?.length);
      setFilterData(data);
    }
  }, [data, langaugeId]);

  const formatDate = (dateStr) => {
    const date = moment(dateStr);
    return date.isValid() ? date.format("Do MMM YYYY") : "NA";
  };

  let columns = [
    {
      accessor: "survey_id",
      Header: "Survey ID",
      align: "center",
      sortable: true,
    },
    {
      accessor: "name",
      Header: "Name",
      sortable: false,
      align: "center",
      headerAlign: "center",
      Cell: ({ row }) => {
        const name = row?.original?.name;
        return (
          <Tooltip title={name}>
            <div
              style={{
                padding: "0 8px",
                whiteSpace: "pre-wrap",
                overflowWrap: "break-word",
                maxWidth: "120px",
                textOverflow: "ellipsis",
                cursor: "pointer",
              }}
            >
              {name}
            </div>
          </Tooltip>
        );
      },
    },
    {
      accessor: "survey_url",
      Header: "Survey URL",
      align: "center",
      headerAlign: "center",
      Cell: ({ row }) => {
        const url = row.original.survey_url;
        return (
          <Tooltip title={url}>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                maxWidth: "100px",
                display: "inline-block",
                wordBreak: "break-word",
              }}
            >
              Visit
            </a>
          </Tooltip>
        );
      },
    },

    {
      accessor: "cpi",
      Header: "CPI",
      sortable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      accessor: "ir",
      Header: "IR",
      sortable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      accessor: "loi",
      Header: "LOI",
      sortable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      accessor: "completes",
      Header: "Completes",
      sortable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      accessor: "language_id",
      Header: "Language ID",
      sortable: true,
      align: "center",
      headerAlign: "center",
    },

    {
      accessor: "desktop_allowed",
      Header: "Desktop",
      align: "center",
      Cell: ({ row }) => (row.original.desktop_allowed ? "Yes" : "No"),
    },
    {
      accessor: "mobile_allowed",
      Header: "Mobile",
      align: "center",
      Cell: ({ row }) => (row.original.mobile_allowed ? "Yes" : "No"),
    },
    {
      accessor: "tablet_allowed",
      Header: "Tablet",
      align: "center",
      Cell: ({ row }) => (row.original.tablet_allowed ? "Yes" : "No"),
    },

    {
      accessor: "start_date",
      Header: "Start Date",
      align: "center",
      Cell: ({ row }) => formatDate(row.original.start_date),
    },
    {
      accessor: "end_date",
      Header: "End Date",
      align: "center",
      Cell: ({ row }) => formatDate(row.original.end_date),
    },
    {
      accessor: "last_updated_on_utc",
      Header: "Last Updated",
      align: "center",
      Cell: ({ row }) => formatDate(row.original.last_updated_on_utc),
    },
    {
      accessor: "qual_last_updated_on_utc",
      Header: "Qual. Updated",
      align: "center",
      Cell: ({ row }) => formatDate(row.original.qual_last_updated_on_utc),
    },
    {
      accessor: "quota_last_updated_on_utc",
      Header: "Quota Updated",
      align: "center",
      Cell: ({ row }) => formatDate(row.original.quota_last_updated_on_utc),
    },

    {
      accessor: "has_qualifications",
      Header: "Qualifications",
      align: "center",
      Cell: ({ row }) => (row.original.has_qualifications ? "Yes" : "No"),
    },
    {
      accessor: "has_quotas",
      Header: "Quotas",
      align: "center",
      Cell: ({ row }) => (row.original.has_quotas ? "Yes" : "No"),
    },
    {
      accessor: "has_survey_groups",
      Header: "Survey Groups",
      align: "center",
      Cell: ({ row }) => (row.original.has_survey_groups ? "Yes" : "No"),
    },
    {
      accessor: "collect_pii",
      Header: "Collect PII",
      align: "center",
      Cell: ({ row }) => (row.original.collect_pii ? "Yes" : "No"),
    },

    {
      accessor: "buyer_id",
      Header: "Buyer ID",
      align: "center",
    },
    {
      accessor: "industry_id",
      Header: "Industry ID",
      align: "center",
    },
    {
      accessor: "study_type_id",
      Header: "Study Type ID",
      align: "center",
    },
  ];

  return (
    <MDBox sx={{ pt: "12px" }}>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <MDBox
        sx={{
          borderRadius: "12px",
          background: "#fff",
          width: "100%",
        }}
      >
        <Grid container sx={{ width: "100%" }}>
          <Grid item xs={12} md={6} lg={2} sx={{ width: "100%", padding: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Language Id</InputLabel>
              <Select
                value={langaugeId}
                label="Language Id"
                onChange={(e) => {
                  setLangaugeId(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value={-1}>All</MenuItem>
                {language?.map((value, i) => (
                  <MenuItem value={value.id} key={i}>
                    {value.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <DataTable
          table={{ columns, rows: filterData }}
          // isSorted
          //   noEndBorder
          entriesPerPage={entriesPerPage}
          canSearch={false}
          tablePage={page}
          setTablePage={setPage}
          //   navigateRow={"/vendors"}
          setEntriesPerPage={setEntriesPerPage}
          totalLength={totalLength}
          pagination={true}
          //   setSortOrder={setSortOrder}
          //   setSortBy={setSortBy}
          rowWidth={"40px"}
          //   sortBy={sortBy}
          //   sortOrder={sortOrder}
          setIsLoading={setIsLoading}
          entriesOption={[50,100,200]}
        />
      </MDBox>
    </MDBox>
  );
};

export default SupplyApiSurvey;
